// The merged admin dashboard. One page for every workspace provider: this shell
// owns the chrome, the shared filters, and the tab routing; each provider section
// owns its own table and actions. It replaces the split /admin and /gcp-admin
// pages, which had grown two different vocabularies for the same job.

import { useCallback, useEffect, useState } from "react";
import type { CSSProperties } from "react";
import {
  Button,
  Container,
  Field,
  Input,
  Kicker,
  Main,
  Modal,
  ModalActions,
  Note,
  Panel,
  Select,
  Skeleton,
  Stat,
  StatRow,
  Subtitle,
  SystemScreen,
  Tab,
  Tabs,
  Title,
  Toast,
  ToastStack,
  TopNav,
  Topbar,
} from "@fwf/fwfa-design-system/react";

import { EMPTY_FILTERS, HEALTH_OPTIONS, STATE_OPTIONS } from "./admin/filters";
import type { FilterState } from "./admin/filters";
import { formatIsoDate } from "./admin/format";
import {
  useActions,
  useAdminData,
  useAtLeastMd,
  useAutoRefresh,
  useConfirm,
  useToasts,
} from "./admin/hooks";
import { providerCounts, summarise } from "./admin/state";
import { PROVIDER_TABS } from "./admin/types";
import type { ProviderTab } from "./admin/types";

import { UsersSection } from "./admin/sections/UsersSection";
import { SpritesSection } from "./admin/sections/SpritesSection";
import { DaytonaSection } from "./admin/sections/DaytonaSection";
import { LambdaSection } from "./admin/sections/LambdaSection";
import { GcpSection } from "./admin/sections/GcpSection";

const TAB_LABELS: Record<ProviderTab, string> = {
  all: "All providers",
  users: "Users",
  sprites: "Sprites",
  daytona: "Daytona",
  lambda: "AWS Lambda",
  gcp: "GCP",
};

/** Notice keys come from the loader, one per optional provider overview. */
const NOTICE_LABELS: Record<string, string> = {
  daytona: "Daytona",
  lambda: "AWS Lambda",
  gcp: "GCP",
};

// The DS gives a nav in a bar the sky mark, which is right where the bar is the
// only nav on the page. Here the provider Tabs below already spend sky on the
// active tab, and two sky underlines in one viewport read as one thing split in
// half. The chrome takes the quieter grey and leaves the structural mark to the
// tabs, which is what the section variant does for the same reason.
const TOPNAV_STYLE = { "--topnav-accent": "var(--fwfa-text-soft)" } as CSSProperties;

const SECTION_GRID_STYLE: CSSProperties = { display: "grid", gap: "var(--fwfa-space-6)" };

const BODY_STYLE = { "--fwfa-stack-gap": "var(--fwfa-space-5)" } as CSSProperties;

// Above the DS scrim (50) so an action's outcome stays readable over a dialog.
const TOAST_STACK_STYLE: CSSProperties = {
  position: "fixed",
  right: "var(--fwfa-space-4)",
  bottom: "var(--fwfa-space-4)",
  zIndex: 60,
};

function readTabFromUrl(): ProviderTab {
  const requested = new URLSearchParams(window.location.search).get("provider") ?? "";
  return (PROVIDER_TABS as readonly string[]).includes(requested)
    ? (requested as ProviderTab)
    : "all";
}

/** Stand-in for the stat strip and the first table while the overviews load. */
function LoadingSkeleton({ atLeastMd }: { atLeastMd: boolean }) {
  return (
    <div style={SECTION_GRID_STYLE} aria-hidden="true">
      <Panel>
        {/* Same register as the real strip below, or the skeleton collapses into
            a shorter placeholder than the content that replaces it. */}
        <StatRow spread={atLeastMd} compact={!atLeastMd}>
          {["workspaces", "running", "stopped", "orphaned", "agents"].map((slot) => (
            <Stat
              key={slot}
              value={<Skeleton style={{ width: "3rem", height: "1.75rem" }} />}
              label={<Skeleton style={{ width: "5rem" }} />}
            />
          ))}
        </StatRow>
      </Panel>
      <Panel>
        <div style={{ display: "grid", gap: "var(--fwfa-space-3)" }}>
          <Skeleton style={{ width: "12rem", height: "1.5rem" }} />
          {[0, 1, 2, 3, 4].map((row) => (
            <Skeleton key={row} style={{ height: "1.25rem" }} />
          ))}
        </div>
      </Panel>
    </div>
  );
}

export default function AdminView() {
  const data = useAdminData();
  const atLeastMd = useAtLeastMd();
  const toasts = useToasts();
  const confirm = useConfirm();
  const actions = useActions(toasts, confirm, data.reload);
  const autoRefresh = useAutoRefresh(data.reloadSilent, () => actions.isBusy);

  const [tab, setTab] = useState<ProviderTab>(readTabFromUrl);
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);

  const { reload } = data;

  // The tab lives in the URL so a view is linkable, and so /gcp-admin can land
  // straight on GCP by redirecting to ?provider=gcp.
  const selectTab = useCallback((next: ProviderTab) => {
    setTab(next);
    const url = new URL(window.location.href);
    if (next === "all") url.searchParams.delete("provider");
    else url.searchParams.set("provider", next);
    window.history.replaceState(null, "", `${url.pathname}${url.search}`);
  }, []);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      // Typing "r" into the search box or a filter select has to stay typing, and
      // Cmd/Ctrl+R belongs to the browser.
      const target = event.target;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement
      ) {
        return;
      }
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key !== "r" && event.key !== "R") return;
      event.preventDefault();
      void reload();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [reload]);

  const reconcile = useCallback(() => {
    void actions.run({
      key: "reconcile",
      url: "/api/admin/gcp/instances/reconcile",
      successTitle: "Reconcile complete",
      failureTitle: "Reconcile failed",
      // The count only exists in the response, so the message comes back from
      // onSuccess rather than being fixed when the action is declared.
      onSuccess: (payload) => {
        const removed = (payload as { removed?: number } | null)?.removed ?? 0;
        return `Removed ${removed} stale ${removed === 1 ? "mapping" : "mappings"}`;
      },
    });
  }, [actions]);

  if (data.error) {
    return (
      <SystemScreen
        tone="danger"
        kicker="Admin"
        title={data.error}
        actions={
          <Button variant="primary" onClick={() => { window.location.href = "/api/auth/login"; }}>
            Re-authenticate
          </Button>
        }
      />
    );
  }

  const summary = summarise(data);
  const counts = providerCounts(data);
  const viewerLabel = data.viewer?.email || data.viewer?.id;
  const sectionProps = { data, filters, actions, toasts };

  const rowCount = tab === "all" ? summary.workspaces : counts[tab];
  const generatedAt =
    tab === "daytona"
      ? data.daytona?.generatedAt
      : tab === "lambda"
        ? data.lambda?.generatedAt
        : tab === "gcp"
          ? data.gcp?.generatedAt
          : data.overview?.generatedAt;

  return (
    <>
      <Topbar>
        <TopNav
          style={TOPNAV_STYLE}
          items={[
            { label: "Admin", href: "/admin", active: true },
            { label: "Open app", href: "/app" },
          ]}
        />
      </Topbar>

      <Main>
        <Container>
          <header className="fwfa-page-header fwfa-page-header--split">
            <div>
              <Kicker>Admin</Kicker>
              <Title as="h1">Workspaces</Title>
              {viewerLabel && <Subtitle>Signed in as {viewerLabel}. Press R to refresh.</Subtitle>}
            </div>
            <div className="fwfa-page-header-actions">
              {data.gcp && (
                <Button
                  variant="secondary"
                  onClick={reconcile}
                  loading={actions.isPending("reconcile")}
                  disabled={actions.isPending("reconcile")}
                >
                  Reconcile
                </Button>
              )}
              <Button
                variant="secondary"
                aria-pressed={autoRefresh.enabled}
                onClick={autoRefresh.toggle}
                title="Refresh every 30 seconds"
              >
                Auto-refresh
                {autoRefresh.enabled && (
                  // Mono and zero-padded so the ticking countdown cannot jog the row.
                  <span className="font-mono text-ink-muted">
                    {" "}
                    {String(autoRefresh.secondsLeft).padStart(2, "0")}s
                  </span>
                )}
              </Button>
              <Button variant="primary" onClick={() => void reload()} loading={data.loading}>
                Refresh
              </Button>
            </div>
          </header>

          {data.loading ? (
            <LoadingSkeleton atLeastMd={atLeastMd} />
          ) : (
            <div className="fwfa-stack" style={BODY_STYLE}>
              <Panel>
                {/* `spread` is the hero register: an equal share per stat, figure
                    centred over caption. Below md it stacks to a column, and five
                    stacked h1 figures run about 500px on a phone, more than half
                    the viewport before the provider tabs. `compact` wraps instead,
                    on the smaller figure. */}
                <StatRow spread={atLeastMd} compact={!atLeastMd}>
                  <Stat value={summary.workspaces} label="Workspaces" />
                  <Stat value={summary.running} label="Running" />
                  <Stat value={summary.stopped} label="Stopped" tone="muted" />
                  <Stat
                    value={summary.orphaned}
                    label="Orphaned"
                    tone={summary.orphaned > 0 ? "danger" : "muted"}
                  />
                  <Stat value={summary.agents} label="Agents live" />
                </StatRow>
              </Panel>

              <Tabs
                tablist
                select={
                  <select
                    className="fwfa-tabs-select"
                    aria-label="Provider"
                    value={tab}
                    onChange={(event) => selectTab(event.target.value as ProviderTab)}
                  >
                    {PROVIDER_TABS.map((option) => (
                      <option key={option} value={option}>
                        {TAB_LABELS[option]}
                      </option>
                    ))}
                  </select>
                }
              >
                {PROVIDER_TABS.map((option) => (
                  <Tab key={option} selected={option === tab} onClick={() => selectTab(option)}>
                    {TAB_LABELS[option]}
                    {option !== "all" && (
                      <span className="font-mono text-ink-muted"> {counts[option]}</span>
                    )}
                  </Tab>
                ))}
              </Tabs>

              {/* State and health say nothing about a user row, so the whole bar goes. */}
              {tab !== "users" && (
                <div className="fwfa-toolbar">
                  <div className="fwfa-toolbar-group">
                    <Field label="State">
                      <Select
                        value={filters.state}
                        onChange={(event) =>
                          setFilters((current) => ({
                            ...current,
                            state: event.target.value as FilterState["state"],
                          }))
                        }
                      >
                        {STATE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Select>
                    </Field>
                    <Field label="Health">
                      <Select
                        value={filters.health}
                        onChange={(event) =>
                          setFilters((current) => ({
                            ...current,
                            health: event.target.value as FilterState["health"],
                          }))
                        }
                      >
                        {HEALTH_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Select>
                    </Field>
                    <Field label="Search" style={{ flex: "1 1 16rem" }}>
                      <Input
                        type="search"
                        value={filters.query}
                        placeholder="Resource, owner, project, slug…"
                        onChange={(event) =>
                          setFilters((current) => ({ ...current, query: event.target.value }))
                        }
                      />
                    </Field>
                  </div>
                  <div className="fwfa-toolbar-group">
                    <p className="fwfa-type-label font-mono">
                      {rowCount} {rowCount === 1 ? "workspace" : "workspaces"}
                    </p>
                    {generatedAt && (
                      <p className="fwfa-type-label fwfa-type-label--soft font-mono">
                        {formatIsoDate(generatedAt)}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {Object.entries(data.notices).map(([provider, message]) => (
                <Note key={provider} variant="warn">
                  <strong>{NOTICE_LABELS[provider] ?? provider}:</strong> {message}
                </Note>
              ))}

              <div style={SECTION_GRID_STYLE}>
                {(tab === "all" || tab === "sprites") && <SpritesSection {...sectionProps} />}
                {(tab === "all" || tab === "daytona") && <DaytonaSection {...sectionProps} />}
                {(tab === "all" || tab === "lambda") && <LambdaSection {...sectionProps} />}
                {(tab === "all" || tab === "gcp") && <GcpSection {...sectionProps} />}
                {(tab === "all" || tab === "users") && <UsersSection {...sectionProps} />}
              </div>
            </div>
          )}
        </Container>
      </Main>

      <Modal
        open={Boolean(confirm.pending)}
        onClose={confirm.cancel}
        labelledBy="admin-confirm-title"
      >
        <h3 id="admin-confirm-title">{confirm.pending?.title}</h3>
        <p>{confirm.pending?.body}</p>
        <ModalActions>
          <Button variant="secondary" onClick={confirm.cancel}>
            Cancel
          </Button>
          <Button
            variant={confirm.pending?.danger ? "danger" : "primary"}
            onClick={confirm.confirm}
          >
            {confirm.pending?.confirmLabel}
          </Button>
        </ModalActions>
      </Modal>

      <ToastStack style={TOAST_STACK_STYLE}>
        {toasts.toasts.map((toast) => (
          <Toast
            key={toast.id}
            tone={toast.tone}
            title={toast.title}
            onDismiss={() => toasts.dismiss(toast.id)}
          >
            {toast.message}
          </Toast>
        ))}
      </ToastStack>
    </>
  );
}
